import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  catchError,
  concatMap,
  EMPTY,
  from,
  last,
  map,
  of,
  retry,
  Subscription,
  tap,
  timer,
  toArray,
} from 'rxjs';
import {
  AudioRecording,
  ConstructionDayReport,
  ConstructionDayReportType,
  MaterialConsumption,
  MachineUsage,
  NodeItem,
  ReportCreationResult,
  StateService,
  TimeEntry,
  ToolService,
  WorkingEntry,
} from 'projects/shared/src/public-api';
import { ModalController } from '@ionic/angular';
import { UploadDocumentRequest } from '../../models/upload-document-reqest';
import { ModalRole } from '../../models/modal-roles';
import { SelectSearchOption } from '../../models/select-search-option';

interface ReportSendResult {
  report: ConstructionDayReport;
  reportCreated: boolean;
  reportCreationResult?: ReportCreationResult;
  failedUploads: UploadDocumentRequest[];
}

@Component({
  selector: 'lib-construction-day-report-form',
  templateUrl: './construction-day-report-form.component.html',
  styleUrls: ['./construction-day-report-form.component.scss'],
  standalone: false,
})
export class ConstructionDayReportFormComponent implements OnInit, OnDestroy {
  private translate = inject(TranslateService);

  constructor(
    public state: StateService,
    public tools: ToolService,
    private modalCtrl: ModalController
  ) {}

  private allConSiteReports: ConstructionDayReport[] = [];
  public rentalMachineOptions: SelectSearchOption<string>[] = [];
  public materialOptions: SelectSearchOption<string>[] = [];
  public existingHoursInDb: WorkingEntry[];
  private subs: Subscription[] = [];
  public reports: ConstructionDayReport[] = [];
  public description: any;
  public weatherOptions: string[] = [
    'Sonne',
    'Bewölkt',
    'Wind',
    'Regen',
    'Schnee',
    'Frost',
    'Hagel',
    'Schneeregen',
    'Graupel',
    'Sturm',
    'Gewitter',
  ];

  public weatherOptionsTranslated = [
    {
      value: 'Sonne',
      label: this.translate.instant('reports.weatherOptions.sunny'),
    },
    {
      value: 'Bewölkt',
      label: this.translate.instant('reports.weatherOptions.cloudy'),
    },
    {
      value: 'Wind',
      label: this.translate.instant('reports.weatherOptions.wind'),
    },
    {
      value: 'Regen',
      label: this.translate.instant('reports.weatherOptions.rain'),
    },
    {
      value: 'Schnee',
      label: this.translate.instant('reports.weatherOptions.snow'),
    },
    {
      value: 'Frost',
      label: this.translate.instant('reports.weatherOptions.frost'),
    },
    {
      value: 'Hagel',
      label: this.translate.instant('reports.weatherOptions.hail'),
    },
    {
      value: 'Schneeregen',
      label: this.translate.instant('reports.weatherOptions.sleet'),
    },
    {
      value: 'Graupel',
      label: this.translate.instant('reports.weatherOptions.graupel'),
    },
    {
      value: 'Sturm',
      label: this.translate.instant('reports.weatherOptions.storm'),
    },
    {
      value: 'Gewitter',
      label: this.translate.instant('reports.weatherOptions.thunderstorm'),
    },
  ];
  public reportId: string;
  public errorList: string[] = [];
  public ConstructionDayReportType = ConstructionDayReportType;
  public reportType = ConstructionDayReportType.$0;
  public reportTypeLabel: string;
  public reportTypePluralLabel: string;
  public get reportTypeSubmitLabel(): string {
    return this.reports.length > 1
      ? this.translate.instant('reports.basicReportPlural')
      : this.translate.instant('reports.basicReport');
  }
  public uploadRequests: UploadDocumentRequest[][] = [];
  public attachedNodeItems: NodeItem[] = [];
  public nodeItemsToDelete: string[] = [];
  private uniqueIds: string[] = [];
  public showAudioInput: boolean = false;
  public sendButtonDisabled: boolean = false;

  ngOnInit() {
    this.changeReportTypeLabel();
    this.subs.push(
      this.state.constructionDayReports$.subscribe((x) => {
        this.allConSiteReports = JSON.parse(JSON.stringify(x));
        this.updateSelectOptions();
      })
    );
    if (this.reportId != null) {
      this.subs.push(
        this.state.constructionDayReports$.subscribe((x) => {
          this.reports.push(
            JSON.parse(JSON.stringify(x.find((x) => x.id == this.reportId)))
          );
          this.reportType = this.reports[0].type;
          this.changeReportTypeLabel();
          this.checkForInvalidFields();
        })
      );
      this.checkForExistingReports(this.reports[0]);
    } else {
      this.addNewReport();
    }
    if (this.reportId) {
      this.subs.push(
        this.state.nodeItems$.subscribe((x) => {
          this.attachedNodeItems = x.filter(
            (x) => x.constructionDayReportId == this.reportId
          );
        })
      );
    }

    this.checkForInvalidFields();
  }

  public async cancel() {
    return await this.modalCtrl.dismiss(null, 'cancel');
  }

  public changeReportTypeLabel() {
    if (this.reportType === ConstructionDayReportType.$0) {
      this.reportTypeLabel = this.translate.instant('reports.daily');
      this.reportTypePluralLabel = this.translate.instant(
        'reports.dailyPlural'
      );
    }
    if (this.reportType === ConstructionDayReportType.$1) {
      this.reportTypeLabel = this.translate.instant('reports.trip');
      this.reportTypePluralLabel = this.translate.instant('reports.tripPlural');
    }
    if (this.reportType === ConstructionDayReportType.$2) {
      this.reportTypeLabel = this.translate.instant('reports.work');
      this.reportTypePluralLabel = this.translate.instant('reports.workPlural');
    }

    this.reports.forEach((x) => {
      this.syncWorkersAndTimes(x);
    });

    this.checkForInvalidFields();
  }

  private updateSelectOptions(): void {
    const uniqueMachineNames = new Map<string, string>();
    const uniqueMaterialNames = new Map<string, string>();
    this.allConSiteReports.forEach((report) => {
      if (report.type !== ConstructionDayReportType.$0) {
        return;
      }
      report.rentalMachines?.forEach((machine) => {
        const name = machine.name?.trim();
        if (name) {
          uniqueMachineNames.set(name.toLowerCase(), name);
        }
      });
      report.materialConsumption?.forEach((material) => {
        const name = material.name?.trim();
        if (name) {
          uniqueMaterialNames.set(name.toLowerCase(), name);
        }
      });
    });
    this.rentalMachineOptions = Array.from(
      uniqueMachineNames.values(),
      (name) => ({
        value: name,
        label: name,
      })
    );
    this.materialOptions = Array.from(uniqueMaterialNames.values(), (name) => ({
      value: name,
      label: name,
    }));
  }

  ngOnDestroy() {
    this.unsubscribeAll();
  }

  private unsubscribeAll() {
    this.subs?.forEach((x) => {
      x.unsubscribe();
    });
  }

  public addNewReport() {
    let newReport: ConstructionDayReport = {};
    newReport.id = '';
    newReport.constructionSite = null;
    newReport.date = null;
    if (this.reports.length > 0) {
      newReport.date = this.reports[this.reports.length - 1].date;
      const lastWorkingTimes =
        this.reports[this.reports.length - 1].workingTimes;
      newReport.workingTimes = [
        {
          start: lastWorkingTimes[lastWorkingTimes.length - 1].end,
          end: this.tools.addOneHour(
            lastWorkingTimes[lastWorkingTimes.length - 1].end
          ),
        },
      ];
    } else {
      newReport.workingTimes = [
        {
          start: this.tools.getRoundedUtcTime(false),
          end: this.tools.addOneHour(this.tools.getRoundedUtcTime(false)),
        },
      ];
    }
    newReport.workers = [];
    newReport.difficulties = '';
    newReport.electricityUsedInKWH = 0;
    newReport.waterUsedInCubicMeters = 0;
    newReport.weather = [];
    newReport.temperatureInCelsius = 0;
    newReport.rapport = '';
    newReport.report = '';
    newReport.responsibleWorker = this.state.userName;
    newReport.sentDate = '';
    newReport.securityCheck = false;
    newReport.specialIncidents = '';
    newReport.approvedBy = '';
    newReport.approvedDate = new Date().toISOString();
    newReport.sentDate = new Date().toISOString();
    newReport.workerTimeEntries = {};
    newReport.machineUsages = [];
    newReport.rentalMachines = [];
    newReport.materialConsumption = [];
    this.uniqueIds.push('');
    this.syncWorkersAndTimes(newReport);
    this.reports.push(newReport);
    this.checkForInvalidFields();
  }

  private syncWorkersAndTimes(report: ConstructionDayReport) {
    if (this.reportType != ConstructionDayReportType.$0) {
      report.workers = [report.responsibleWorker];
      report.workerTimeEntries = {};
      report.workerTimeEntries[report.responsibleWorker] = report.workingTimes;
    }
  }

  public timeEntryStartChanged(
    entry: TimeEntry,
    evt: string,
    report: ConstructionDayReport
  ) {
    entry.start = evt;
    this.syncWorkersAndTimes(report);
    this.checkForInvalidFields();
  }

  public timeEntryEndChanged(
    entry: TimeEntry,
    evt: string,
    report: ConstructionDayReport
  ) {
    entry.end = evt;
    this.syncWorkersAndTimes(report);
    this.checkForInvalidFields();
  }

  public addTimeEntry(entries: TimeEntry[], report: ConstructionDayReport) {
    if (entries.length == 0) {
      entries.push({});
      this.checkForInvalidFields();
      return;
    }
    const lastValue = entries[entries.length - 1];
    const start = this.tools.addOneHour(lastValue.end);
    entries.push({ start: start, end: this.tools.addOneHour(start) });
    this.checkForInvalidFields();
  }

  public removeTimeEntry(entries: TimeEntry[], i: number) {
    entries.splice(i, 1);
    this.checkForInvalidFields();
  }

  public removeReport(i: number) {
    this.reports.splice(i, 1);
    this.checkForInvalidFields();
  }

  public weatherChange(e: any, report: ConstructionDayReport) {
    report.weather = e.detail.value;
  }
  public workerChange(e: any, report: ConstructionDayReport) {
    report.workers = e;
    e.forEach((x: string) => {
      if (report.workerTimeEntries[x] == null) {
        report.workerTimeEntries[x] = JSON.parse(
          JSON.stringify(report.workingTimes)
        );
      }
    });
    for (let key in report.workerTimeEntries) {
      if (!report.workers.includes(key)) delete report.workerTimeEntries[key];
    }
    this.checkForInvalidFields();
  }
  public responsibleWorkerChange(e: any, report: ConstructionDayReport) {
    report.responsibleWorker = e;
    this.syncWorkersAndTimes(report);
    this.checkForInvalidFields();
  }

  public conSiteChange(e: any, report: ConstructionDayReport) {
    report.constructionSite = e;
    this.checkForExistingReports(report);
    this.checkForInvalidFields();
  }

  public dateChanged(evt: string, report: ConstructionDayReport) {
    report.date = evt;
    this.checkForExistingReports(report);
    this.checkForInvalidFields();
  }

  private checkForExistingReports(report: ConstructionDayReport) {
    if (report.constructionSite && report.date) {
      this.subs.push(
        this.state
          .constructionDayReportExists(report.constructionSite, report.date)
          .subscribe((y) => {
            this.existingHoursInDb = y;
            this.checkForInvalidFields();
          })
      );
    }
  }

  public sendToBackend(isDraft: boolean = false) {
    this.reports.forEach((x) => {
      x.isDraft = isDraft;
      if (!x.temperatureInCelsius) {
        x.temperatureInCelsius = 0;
      } else {
        x.temperatureInCelsius = parseInt(x.temperatureInCelsius.toString());
      }
      if (!x.electricityUsedInKWH) x.electricityUsedInKWH = 0;
      if (!x.waterUsedInCubicMeters) x.waterUsedInCubicMeters = 0;
      x.type = this.reportType;
    });

    if (this.reportId == null) this.createReports();

    if (this.reportId != null) this.updateReport(this.reports[0]);
  }

  // we could use a forkJoin here
  private async updateReport(report: ConstructionDayReport) {
    this.state
      .updateConstructionDayReport(report)
      .pipe(
        concatMap((x: string) => {
          if (
            this.uploadRequests.length === 0 ||
            this.uploadRequests[0].length === 0
          )
            return of(undefined);
          this.unsubscribeAll();
          this.tools.showInfoToast(
            this.translate.instant('reports.documentsUploading')
          );
          this.uploadRequests[0].forEach(
            (x) => (x.constructionDayReportId = this.reportId)
          );
          return from(this.uploadRequests[0]).pipe(
            concatMap((file) => this.state.uploadFile(file)),
            last()
          );
        }),

        catchError((err) => {
          this.state.handleError(err);
          return EMPTY;
        })
      )
      .subscribe({
        next: async (v) => {
          this.tools.showInfoToast(
            this.translate.instant('reports.updateSuccess')
          );
          await this.modalCtrl.dismiss(null, ModalRole.Confirm);
        },
        error: (err) => this.state.handleError(err),
      });

    await this.state.deleteNodes(this.nodeItemsToDelete, false);
  }

  private createReports() {
    const results: ReportSendResult[] = [];
    this.sendButtonDisabled = true;
    from(this.reports)
      .pipe(
        concatMap((report, reportIndex) => {
          const uploads = this.uploadRequests[reportIndex] ?? [];
          return this.state.createConstructionDayReport(report).pipe(
            retry({
              count: 2,
            }),
            map(
              (reportCreationResult: ReportCreationResult) =>
                ({
                  report,
                  reportCreated: true,
                  reportCreationResult,
                  failedUploads: [],
                } as ReportSendResult)
            ),

            catchError((err) => {
              this.state.handleError(err);
              const errorMessage = this.translate.instant(
                'reports.submitError',
                { number: this.reports.length > 1 ? reportIndex + 1 : '' }
              );
              this.tools.showErrorToast(errorMessage);
              this.sendButtonDisabled = false;

              return of({
                report,
                reportCreated: false,
                failedUploads: [],
                reportError: err,
              } as ReportSendResult);
            })
          );
        }),

        /*
         * ============================================================
         * 2. ANHÄNGE HOCHLADEN
         * ============================================================
         *
         * Wird nur ausgeführt, wenn der Bericht erfolgreich erstellt
         * wurde.
         */
        concatMap((result: ReportSendResult) => {
          if (!result.reportCreated) {
            return of(result);
          }

          const uploads =
            this.uploadRequests[this.reports.indexOf(result.report)] ?? [];

          if (uploads.length === 0) {
            return of(result);
          }

          this.tools.showInfoToast(
            this.translate.instant('reports.createdUploading')
          );

          /*
           * Die ID des neu erstellten Berichts an alle Anhänge hängen.
           */
          uploads.forEach(
            (upload) =>
              (upload.constructionDayReportId = result.reportCreationResult!.id)
          );

          /*
           * Jeden Upload einzeln behandeln.
           *
           * Ein Fehler bei einem Upload darf NICHT die anderen Uploads
           * abbrechen.
           */
          return from(uploads).pipe(
            concatMap((upload) =>
              this.state.uploadFile(upload).pipe(
                retry({
                  count: 2,
                }),
                map(() => ({
                  upload,
                  success: true,
                })),

                /*
                 * Upload fehlgeschlagen
                 *
                 * Wir fangen den Fehler hier ab und machen mit dem
                 * nächsten Upload weiter.
                 */
                catchError((err) => {
                  this.state.handleError(err);

                  return of({
                    upload,
                    success: false,
                  });
                })
              )
            ),

            /*
             * Alle Upload-Ergebnisse sammeln
             */
            toArray(),

            /*
             * Nur die fehlgeschlagenen Uploads behalten
             */
            map((uploadResults) => {
              const failedUploads = uploadResults
                .filter((result) => !result.success)
                .map((result) => result.upload);

              return {
                ...result,
                failedUploads,
              };
            })
          );
        })
      )
      .subscribe({
        next: (result: ReportSendResult) => {
          results.push(result);
          this.tools.showInfoToast(
            this.generateSuccessMesage(
              result.report.type,
              result.reportCreationResult.merged
            )
          );
        },

        error: (err) => {
          this.state.handleError(err);
        },

        complete: () => {
          /*
           * ============================================================
           * AUSWERTUNG
           * ============================================================
           */

          const failedReports = results.filter(
            (result) => !result.reportCreated
          );

          const reportsWithFailedUploads = results.filter(
            (result) => result.reportCreated && result.failedUploads.length > 0
          );

          const failedUploadCount = reportsWithFailedUploads.reduce(
            (sum, result) => sum + result.failedUploads.length,
            0
          );

          if (failedUploadCount > 0) {
            this.tools.showErrorToast(
              this.translate.instant('upload.attachmentsFailed', {
                count: failedUploadCount,
              })
            );
          }

          /*
           * Alles erfolgreich
           */
          if (failedReports.length === 0) {
            this.modalCtrl.dismiss(null, ModalRole.Confirm);
            console.log(results);
            return;
          }

          /*
           * ============================================================
           * FEHLGESCHLAGENE BERICHTE
           * ============================================================
           */

          this.reports = failedReports.map((result) => result.report);

          /*
           * ============================================================
           * FEHLGESCHLAGENE ANHÄNGE
           * ============================================================
           *
           * Die Uploads bleiben eindeutig ihrem Bericht zugeordnet,
           * weil die constructionDayReportId bereits gesetzt wurde.
           */
          /*console.log()
          this.uploadRequests = reportsWithFailedUploads.map(
            (result) => result.failedUploads
          );*/

          /*
           * ============================================================
           * BENUTZERINFORMATION
           * ============================================================
           */

          if (failedReports.length > 0) {
            this.tools.showErrorToast(
              this.translate.instant('reports.failed', {
                count: failedReports.length,
              })
            );
          }

          console.log(results);
        },
      });
  }

  public createDraft() {
    let allConSitesSet = true;
    this.reports.forEach((x) => {
      if (!x.constructionSite) {
        allConSitesSet = false;
      }
    });
    if (allConSitesSet) {
      this.sendToBackend(true);
    } else {
      this.tools.showInfoToast(
        this.translate.instant('reports.selectConstructionSite')
      );
    }
  }

  isTimeGreater(date1: Date, date2: Date) {
    const t1 =
      date1.getHours() * 3600 + date1.getMinutes() * 60 + date1.getSeconds();
    const t2 =
      date2.getHours() * 3600 + date2.getMinutes() * 60 + date2.getSeconds();
    return t1 > t2;
  }

  public checkForInvalidFields() {
    this.errorList = [];
    for (let i = 0; i < this.reports.length; i++) {
      for (let j = i + 1; j < this.reports.length; j++) {
        this.reports[i].workers.forEach((x) => {
          if (this.reports[j].workers.includes(x)) {
            if (
              this.tools.arraysOverlap(
                this.reports[i].workerTimeEntries[x],
                this.reports[j].workerTimeEntries[x]
              ) &&
              this.tools.dateStringsAreSameDay(
                this.reports[i].date,
                this.reports[j].date
              )
            ) {
              this.errorList.push(
                this.translate.instant('reports.timesOverlapBetweenReports', {
                  name: this.state.getUserDisplayName(x),
                  first: i + 1,
                  second: j + 1,
                })
              );
            }
          }
        });
      }
    }
    this.reports.forEach((report, i) => {
      if (this.tools.timeEntriesInvalid(report.workingTimes)) {
        this.errorList.push(this.translate.instant('reports.invalidSiteTimes'));
      }
      if (!report.constructionSite)
        this.errorList.push(this.translate.instant('reports.noSiteSelected'));
      if (
        !report.securityCheck &&
        this.reportType == ConstructionDayReportType.$0
      )
        this.errorList.push(
          this.translate.instant('reports.securityCheckMissing')
        );
      if (!report.responsibleWorker)
        this.errorList.push(
          this.translate.instant('reports.responsibleMissing')
        );
      if (!report.report)
        this.errorList.push(this.translate.instant('reports.resultsMissing'));
      if (report.workingTimes.length == 0)
        this.errorList.push(this.translate.instant('reports.siteTimesMissing'));
      if (
        report.workers.length == 0 &&
        this.reportType == ConstructionDayReportType.$0
      )
        this.errorList.push(
          this.translate.instant('reports.noEmployeesSelected')
        );
      if (this.tools.arrayHasOverlap(report.workingTimes))
        this.errorList.push(this.translate.instant('reports.siteTimesOverlap'));
      if (report.workers.length > 0 && report.workingTimes.length > 0) {
        report.workers.forEach((x) => {
          if (this.timeCollisionDetected(x, report)) {
            this.errorList.push(
              this.translate.instant('reports.timeOverlapsBooked', {
                name: this.state.getUserDisplayName(x),
              })
            );
          }
          if (this.tools.arrayHasOverlap(report.workerTimeEntries[x]))
            this.errorList.push(
              this.translate.instant('reports.employeeTimesOverlap', {
                name: this.state.getUserDisplayName(x),
              })
            );

          if (report.workerTimeEntries[x].length == 0) {
            this.errorList.push(
              this.translate.instant('reports.noTimesForEmployee', {
                name: this.state.getUserDisplayName(x),
              })
            );
          }

          if (
            this.tools.hasInvalidBookedTimes(
              report.workingTimes,
              report.workerTimeEntries[x]
            )
          ) {
            this.errorList.push(
              this.translate.instant('reports.timesOutsideSiteTimes', {
                name: this.state.getUserDisplayName(x),
              })
            );
          }
        });
      }
      report.machineUsages?.forEach((x) => {
        if (!x.id)
          this.errorList.push(
            this.translate.instant('reports.machineSelectionMissing')
          );
        if (!x.usageInHours || x.usageInHours < 0)
          this.errorList.push(
            this.translate.instant('reports.machineHoursMissing')
          );
      });
      report.rentalMachines?.forEach((x) => {
        if (!x.name)
          this.errorList.push(
            this.translate.instant('reports.rentalNameMissing')
          );
        if (!x.usageInHours || x.usageInHours < 0)
          this.errorList.push(
            this.translate.instant('reports.rentalHoursMissing')
          );
      });
      report.materialConsumption?.forEach((x) => {
        if (!x.name)
          this.errorList.push(
            this.translate.instant('reports.materialNameMissing')
          );
        if (!x.amount)
          this.errorList.push(
            this.translate.instant('reports.materialAmountMissing')
          );
        if (!x.unit)
          this.errorList.push(
            this.translate.instant('reports.materialUnitMissing')
          );
      });
    });
  }

  userHasOtherConSiteWorkingTimes(
    worker: string,
    report: ConstructionDayReport
  ): boolean {
    return (
      this.existingHoursInDb?.find(
        (x) =>
          x.user == worker &&
          x.createdBy != this.reportId &&
          (x.constructionSite != report.constructionSite ||
            (x.constructionSite == report.constructionSite &&
              x.createdByReport))
      ) != null
    );
  }

  userHasWorkingTimes(worker: string, report: ConstructionDayReport): boolean {
    let usrWorkingTimes = this.existingHoursInDb?.find(
      (x) =>
        x.user == worker &&
        x.constructionSite == report.constructionSite &&
        !x.createdByReport
    );
    if (this.reportType == ConstructionDayReportType.$0) {
      return (
        usrWorkingTimes != null &&
        JSON.stringify(usrWorkingTimes.timeEntries) !=
          JSON.stringify(report.workerTimeEntries[worker])
      );
    } else {
      return (
        usrWorkingTimes != null &&
        JSON.stringify(usrWorkingTimes.timeEntries) !=
          JSON.stringify(report.workingTimes)
      );
    }
  }

  getThisConSiteWorkingTimes(
    worker: string,
    report: ConstructionDayReport
  ): WorkingEntry[] {
    let workingList = '';
    return this.existingHoursInDb.filter(
      (x) =>
        x.user == worker &&
        x.constructionSite == report.constructionSite &&
        !x.createdByReport
    );
  }

  getBeautifiedString(x: WorkingEntry) {
    const times = x.timeEntries
      .map((entry) =>
        this.translate.instant('reports.timeRange', {
          start: this.tools.formatTime(entry.start),
          end: this.tools.formatTime(entry.end),
        })
      )
      .join(', ');
    return this.translate.instant('reports.manualTimeFound', { times });
  }

  getOtherConSiteWorkingTimes(
    worker: string,
    report: ConstructionDayReport
  ): string[] {
    let workingList: string[] = [];
    this.existingHoursInDb
      .filter(
        (x) =>
          x.user == worker &&
          x.createdBy != this.reportId &&
          (x.constructionSite != report.constructionSite ||
            (x.constructionSite == report.constructionSite &&
              x.createdByReport))
      )
      .forEach((x) => {
        let entry =
          this.state.getConstructionSiteName(x.constructionSite) + ': ';
        entry += x.timeEntries
          .map(
            (y) =>
              this.tools.formatTime(y.start) +
              ' bis ' +
              this.tools.formatTime(y.end)
          )
          .join(', ');
        workingList.push(entry);
      });
    return workingList;
  }

  takeUserHours(
    worker: string,
    entry: WorkingEntry,
    report: ConstructionDayReport
  ) {
    report.workerTimeEntries[worker] = JSON.parse(
      JSON.stringify(entry.timeEntries)
    );
    if (this.reportType != ConstructionDayReportType.$0) {
      report.workingTimes = JSON.parse(JSON.stringify(entry.timeEntries));
    }
    if (!report.report.includes(entry.description))
      report.report += '\n' + entry.description;
    this.checkForInvalidFields();
  }

  timeCollisionDetected(
    worker: string,
    report: ConstructionDayReport
  ): boolean {
    if (!this.existingHoursInDb) return false;
    let doesOverlap = false;
    this.existingHoursInDb
      .filter(
        (x) =>
          x.user == worker && x.createdBy != this.reportId && x.createdByReport
      )
      .forEach((x) => {
        if (
          this.tools.arraysOverlap(
            x.timeEntries,
            report.workerTimeEntries[worker]
          )
        )
          doesOverlap = true;
      });
    return doesOverlap;
  }

  public weekendOrPublicHolidaySelected(date: string): boolean {
    const dt = new Date(date);
    return dt.getDay() == 0 || dt.getDay() == 6;
  }

  public setUploadFiles(
    uploadRequests: UploadDocumentRequest[],
    index: number
  ) {
    this.uploadRequests[index] = uploadRequests;
  }

  public openFileInput(index: number) {
    document.getElementById(this.uniqueIds[index]).click();
  }

  private generateSuccessMesage(
    reportType: ConstructionDayReportType,
    merged: boolean
  ): string {
    let message = '';
    if (reportType == ConstructionDayReportType.$0) {
      message = this.translate.instant('reports.dailySubmitSuccess');
    } else {
      let reportName =
        reportType == ConstructionDayReportType.$1
          ? this.translate.instant('reports.trip')
          : this.translate.instant('reports.work');
      if (merged) {
        message = this.translate.instant('reports.mergedSuccess', {
          name: reportName,
        });
      } else {
        message = this.translate.instant('reports.submitSuccess', {
          name: reportName,
        });
      }
    }
    return message;
  }

  public deleteNodeItem(id: string) {
    this.nodeItemsToDelete.push(id);
  }

  public setUniqueId(id: string, index: number) {
    this.uniqueIds[index] = id;
  }

  public recordingCreated(audio: AudioRecording, reportIndex: number) {
    this.subs.push(
      this.state.sendConstructionDayReportAudioRecording(audio).subscribe({
        next: (result) => {
          console.log(result);
          this.showAudioInput = false;
          this.reports[reportIndex] = result;
          this.checkForInvalidFields();
        },
        error: (err) => {
          this.state.handleError(err);
        },
      })
    );
  }

  public selectedMachineChanged(
    id: string | null,
    machine: MachineUsage,
    report: ConstructionDayReport
  ) {
    machine.id = id;
    this.checkForInvalidFields();
  }

  public addMachine(report: ConstructionDayReport) {
    if (!report.machineUsages) report.machineUsages = [];
    report.machineUsages.push({});
    this.checkForInvalidFields();
  }

  public removeMachine(report: ConstructionDayReport, index: number) {
    if (!report.machineUsages) report.machineUsages = [];
    report.machineUsages.splice(index, 1);
    this.checkForInvalidFields();
  }

  public addRentalMachine(report: ConstructionDayReport) {
    if (!report.rentalMachines) report.rentalMachines = [];
    report.rentalMachines.push({});
    this.checkForInvalidFields();
  }

  public removeRentalMachine(report: ConstructionDayReport, index: number) {
    report.rentalMachines.splice(index, 1);
    this.checkForInvalidFields();
  }

  public addMaterialConsumption(report: ConstructionDayReport) {
    if (!report.materialConsumption) report.materialConsumption = [];
    report.materialConsumption.push({});
    this.checkForInvalidFields();
  }

  public removeMaterialConsumption(
    report: ConstructionDayReport,
    index: number
  ) {
    report.materialConsumption.splice(index, 1);
    this.checkForInvalidFields();
  }

  public unitChanged(unit: string, consumption: MaterialConsumption) {
    consumption.unit = unit;
    this.checkForInvalidFields();
  }
}
